import NiceModal, { NiceModalHocProps, register, useModal } from "@ebay/nice-modal-react";
import { Dialog, DialogContent } from "@mui/material";
import { Dispatch, FC, SetStateAction, useCallback, useState } from "react";
import { useTypedTranslation } from "../../hooks";
import { DummyComponent } from "../MuiHelpers";
import { Modals } from "./modals";
import * as StyledModals from "./Modals.styled";
import * as Styled from "./ModalWith2Buttons.styled";

const modalWith2ButtonsDefaultComponents = {
    DialogContentWrapperComponent: DialogContent,
    DialogActionsWrapperComponent: StyledModals.ModalDialogActions,
    DialogModalWrapperComponent: StyledModals.ModalPaper,
};

type Components = {
    DialogContentWrapperComponent: React.ElementType<React.PropsWithChildren<{}>>;
    DialogActionsWrapperComponent: React.ElementType<React.PropsWithChildren<{}>>;
    DialogModalWrapperComponent: React.ElementType<React.PropsWithChildren<{ width: string | number }>>;
};

export type ModalWith2ButtonsProps = {
    title: string;
    content: FC<{
        setReturnPayload: Dispatch<SetStateAction<object | undefined>>;
        setButton2Disabled: Dispatch<SetStateAction<boolean>>;
    }>;
    button1Text?: string;
    button2Text?: string;
    width?: number;
    resetReturnPayloadOnClose?: boolean;
    components?: Partial<Components>;
};

/// NOTE: if modal is closed by BackDrop click, resolved with NULL value
export const ModalWith2Buttons: FC<ModalWith2ButtonsProps & NiceModalHocProps> =
    NiceModal.create<ModalWith2ButtonsProps>(
        ({ title, content, button1Text, button2Text, width = 480, resetReturnPayloadOnClose = true, components }) => {
            const modal = useModal();
            const { t } = useTypedTranslation();
            const [returnPayload, setReturnPayload] = useState<any | undefined>(undefined);
            const [button2Disabled, setButton2Disabled] = useState(false);

            const {
                DialogActionsWrapperComponent,
                DialogContentWrapperComponent,
                DialogModalWrapperComponent,
            }: Components = {
                ...modalWith2ButtonsDefaultComponents,
                ...components,
            };

            const handleButtonCancelClick = useCallback(() => {
                modal.resolve(false);
                modal.hide();
            }, [modal]);

            const handleButtonContinueClick = useCallback(() => {
                modal.resolve(returnPayload ?? true);
                resetReturnPayloadOnClose && setReturnPayload(undefined);
                modal.hide();
            }, [returnPayload, resetReturnPayloadOnClose, modal]);

            const ContentComponent = content;

            return (
                <Dialog
                    PaperComponent={DummyComponent}
                    open={modal.visible}
                    onClose={() => {
                        modal.resolve(null);
                        modal.hide();
                    }}
                >
                    <DialogModalWrapperComponent width={width}>
                        <StyledModals.ModalDialogTitle strong>{title}</StyledModals.ModalDialogTitle>
                        <DialogContentWrapperComponent>
                            <ContentComponent
                                setReturnPayload={setReturnPayload}
                                setButton2Disabled={setButton2Disabled}
                            />
                        </DialogContentWrapperComponent>
                        <DialogActionsWrapperComponent>
                            <Styled.ModalButton1 onClick={handleButtonCancelClick}>
                                {button1Text ?? t("global.cancel")}
                            </Styled.ModalButton1>
                            <Styled.ModalButton2 onClick={handleButtonContinueClick} disabled={button2Disabled}>
                                {button2Text ?? t("global.continue")}
                            </Styled.ModalButton2>
                        </DialogActionsWrapperComponent>
                    </DialogModalWrapperComponent>
                </Dialog>
            );
        },
    );

register(Modals.ModalWith2Buttons, ModalWith2Buttons);
